/*
 * ocrPixelOps.ts
 *
 * Pure-function pixel operations shared between browser OCR (detailOcr,
 * pokegenieOcr, etc.) and the Node test harness. Zero DOM / canvas
 * dependencies - all functions take raw RGBA byte arrays.
 *
 * RGBA layout: 4 bytes per pixel, row-major, top-down. width * height * 4
 * total bytes.
 *
 * Exposing these as pure functions means we can:
 *   1. Unit-test against real screenshots in Node (via sharp -> RGBA).
 *   2. Reuse the exact same logic in the browser (canvas getImageData
 *      returns the same RGBA layout).
 *   3. Tune thresholds in one place.
 */

import type { PokedexVariant } from "./pogoPokedexVariantClassifier";

// ── IV bar pixel classifier ──────────────────────────────────────────────────

/**
 * Classify a single pixel as bar-fill (orange/red/pink), bar-empty (light grey),
 * or neither (background, text, divider gap).
 *
 * Bar fill colour samples (hand-measured against detail screenshots):
 *   orange:  R≈245 G≈170 B≈75   (default state, mid-IV stats)
 *   red:     R≈190 G≈80  B≈75   (when stat is 0/15 — visible on Marshtomp HP)
 *   pink:    R≈240 G≈170 B≈170  (some hp variants)
 *   gradient edges: lighter shades, e.g. R=240 G=210 B=170
 * Bar empty colour:
 *   grey:    R≈226 G≈225 B≈230  (uniform light grey, sometimes lavender tinted)
 * Card background:
 *   white:   R≈255 G≈255 B≈255 (must distinguish from light empty grey)
 *   pinkish-card: R=255 G=230 B=215 (gradient lower portion of card)
 */
export function pixelClass(r: number, g: number, b: number): "fill" | "empty" | "other" {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const sat = max === 0 ? 0 : (max - min) / max;
  if (max < 80) return "other";
  // Empty (bar inactive): low-to-moderate saturation + grey-band brightness.
  // Lavender-tinged greys (R=226 G=225 B=230) and warm greys (R=240 G=240 B=240)
  // both must qualify. White card pixels (>=250) must NOT.
  if (sat < 0.08 && max >= 200 && max <= 248) return "empty";
  // Filled (orange/red): warm hue with red-channel dominance over blue.
  // Loosened over original to catch gradient edges (R=242 G=210 B=170).
  if (r >= 180 && r - b >= 35 && r >= g - 10 && b < 200) return "fill";
  return "other";
}

export function isBarRedDominant(r: number, g: number, b: number): boolean {
  // Solid red bar (0/15 lo-IV state): R moderately high, G/B both low.
  // Marshtomp HP: r=190 g=80 b=75 — much darker than 'orange' fill.
  return r > 170 && g < 130 && b < 130 && r - g > 60 && r - b > 60;
}

// ── IV bar sampler ───────────────────────────────────────────────────────────

export interface BarSampleResult {
  /** 0-15 IV value resolved from the bar's fill ratio. */
  value: number;
  /** Was the bar red (i.e. 0/15 or 15/15)? */
  red: boolean;
  /** Confidence 0-100. */
  confidence: number;
  /** Raw fill ratio 0-1 (for debugging). */
  fillRatio: number;
  /** Bar pixel length, for debugging. */
  barLen: number;
}

/**
 * Find the 3 IV bars in a card-region RGBA buffer and sample each.
 *
 * Strategy:
 *   1. Per-row bar score: count fill+empty pixels.
 *   2. Group adjacent high-score rows into bands. We expect 3 bands
 *      (Attack, Defense, HP top-to-bottom).
 *   3. For each band, find left/right edges of the bar and sample 3 rows
 *      through the centre, taking the longest fill run as the answer.
 */
export function sampleIvBarsFromRgba(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
): BarSampleResult[] | null {
  if (width < 50 || height < 30) return null;

  // Step 1: per-row bar score.
  const rowScores = new Array<number>(height).fill(0);
  for (let y = 0; y < height; y++) {
    let score = 0;
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      const cls = pixelClass(data[i], data[i + 1], data[i + 2]);
      if (cls === "fill" || cls === "empty") score++;
    }
    rowScores[y] = score;
  }

  // Step 2: band detection.
  const threshold = Math.floor(width * 0.30);
  const inBar = rowScores.map((s) => s >= threshold);
  const bands: { start: number; end: number }[] = [];
  let bandStart = -1;
  for (let y = 0; y < height; y++) {
    if (inBar[y] && bandStart === -1) bandStart = y;
    else if (!inBar[y] && bandStart !== -1) {
      bands.push({ start: bandStart, end: y - 1 });
      bandStart = -1;
    }
  }
  if (bandStart !== -1) bands.push({ start: bandStart, end: height - 1 });

  const filtered = bands.filter((b) => {
    const thick = b.end - b.start + 1;
    return thick >= 3 && thick <= Math.floor(height * 0.25);
  });
  if (filtered.length < 3) return null;

  // Pick the LONGEST 3 bands (in case label text or bottom margin produced spurious bands).
  // Sort by thickness desc, take top 3, re-sort by y asc to keep ATK/DEF/HP order.
  const top3 = [...filtered]
    .sort((a, b) => (b.end - b.start) - (a.end - a.start))
    .slice(0, 3)
    .sort((a, b) => a.start - b.start);

  // Step 3: sample each band.
  // For each band, identify the bar's full geometric extent (xLeft..xRight)
  // by scanning multiple rows in the band and taking the WIDEST extent that
  // contains any fill or empty pixel. Then count fill pixels across the
  // entire extent (not just longest contiguous run) — this handles the
  // 3-segment chunked bar style with gaps between segments.
  const results: BarSampleResult[] = [];
  for (const band of top3) {
    const cy = Math.floor((band.start + band.end) / 2);
    const sampleYs: number[] = [];
    for (let dy = -1; dy <= 1; dy++) {
      const sy = cy + dy;
      if (sy >= band.start && sy <= band.end) sampleYs.push(sy);
    }
    if (sampleYs.length === 0) sampleYs.push(cy);

    // Find the bar extent by detecting the LONGEST contiguous run of
    // fill+empty pixels, allowing small gaps (≤5px) of "other" pixels
    // (anti-aliasing transitions). This skips the favourite/lucky ribbon
    // badge that often appears as a small red fragment to the left.
    let xLeft = -1;
    let xRight = -1;
    let bestRunWidth = 0;
    for (const sy of sampleYs) {
      let runStart = -1;
      let runEnd = -1;
      let gapRun = 0;
      const maxGap = 6;
      for (let x = 0; x < width; x++) {
        const i = (sy * width + x) * 4;
        const cls = pixelClass(data[i], data[i + 1], data[i + 2]);
        if (cls === "fill" || cls === "empty") {
          if (runStart === -1) runStart = x;
          runEnd = x;
          gapRun = 0;
        } else {
          gapRun++;
          if (gapRun > maxGap && runStart !== -1) {
            const w0 = runEnd - runStart + 1;
            if (w0 > bestRunWidth) {
              bestRunWidth = w0;
              xLeft = runStart;
              xRight = runEnd;
            }
            runStart = -1;
            runEnd = -1;
            gapRun = 0;
          }
        }
      }
      if (runStart !== -1) {
        const w0 = runEnd - runStart + 1;
        if (w0 > bestRunWidth) {
          bestRunWidth = w0;
          xLeft = runStart;
          xRight = runEnd;
        }
      }
    }
    if (xLeft === -1 || xRight - xLeft < 50) {
      results.push({ value: 0, red: false, confidence: 0, fillRatio: 0, barLen: 0 });
      continue;
    }
    const barLen = xRight - xLeft + 1;

    // For each sample row, count total fill pixels across the bar's extent.
    // Take the row with the highest fill count (most confident sample).
    let bestFillCount = 0;
    let bestRedHits = 0;
    let bestSampleSize = 0;
    for (const sy of sampleYs) {
      let fills = 0;
      let redHits = 0;
      let sampled = 0;
      for (let x = xLeft; x <= xRight; x++) {
        const i = (sy * width + x) * 4;
        const r = data[i], g = data[i + 1], b = data[i + 2];
        const cls = pixelClass(r, g, b);
        sampled++;
        if (cls === "fill") {
          fills++;
          if (isBarRedDominant(r, g, b)) redHits++;
        }
      }
      if (fills > bestFillCount) {
        bestFillCount = fills;
        bestRedHits = redHits;
        bestSampleSize = sampled;
      }
    }

    const fillRatio = barLen > 0 ? bestFillCount / barLen : 0;
    // Snap to 1/15 step. Apply a small "15/15 nudge": ratios >= 0.93 are
    // visually full bars (gradient edges + rounded corners eat ~5% of width).
    let value: number;
    if (fillRatio >= 0.93) value = 15;
    else if (fillRatio <= 0.03) value = 0;
    else value = Math.round(fillRatio * 15);
    if (value < 0) value = 0;
    if (value > 15) value = 15;
    // Red detection: dominant solid-red sample means lo-IV (0/15) state.
    const redRatio = bestSampleSize > 0 ? bestRedHits / bestSampleSize : 0;
    const red = redRatio > 0.20;
    if (red && fillRatio < 0.50) {
      // Solid red short-fill bars represent 0/15 (Marshtomp HP).
      // Solid red full bars don't exist in practice — full = orange.
      value = 0;
    }
    const stepDist = Math.abs(fillRatio * 15 - value);
    const stepConf = 100 - Math.min(60, Math.floor(stepDist * 60));
    const lengthConf = barLen >= 100 ? 100 : Math.floor((barLen / 100) * 100);
    const confidence = Math.floor((stepConf * 0.7) + (lengthConf * 0.3));
    results.push({ value, red, confidence, fillRatio, barLen });
  }
  return results;
}

// ── Average RGB sampler ──────────────────────────────────────────────────────

export function avgRgbFromRgba(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
): { r: number; g: number; b: number } | null {
  if (width < 1 || height < 1) return null;
  let r = 0, g = 0, b = 0, n = 0;
  for (let i = 0; i < data.length; i += 4) {
    r += data[i];
    g += data[i + 1];
    b += data[i + 2];
    n++;
  }
  if (n === 0) return null;
  return { r: r / n, g: g / n, b: b / n };
}

// ── Specular density (shiny detection) ───────────────────────────────────────

export function specularDensityFromRgba(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
): number {
  if (width < 2 || height < 2) return 0;
  let bright = 0, total = 0;
  for (let i = 0; i < data.length; i += 16) {
    const r = data[i], g = data[i + 1], b = data[i + 2];
    total++;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    if (max > 235 && (max - min) <= 40) bright++;
  }
  return total > 0 ? bright / total : 0;
}

// ── Appraisal stars (3-segment gold pixel density) ──────────────────────────

/**
 * Test whether a pixel is the saturated gold colour of a filled appraisal star.
 */
function isAppraisalGold(r: number, g: number, b: number): boolean {
  // Filled star is roughly R=255 G=200 B=80, with anti-aliased halo R=255 G=215 B=140.
  // Reject non-yellow (high B), reject mid-grey (R≈G≈B), reject orange-bar
  // colour (R~245 G~170 B~75 — G/R ratio higher in stars).
  if (r < 220) return false;
  if (b > 160) return false;
  if (g < 150) return false;
  if (r - b < 80) return false;
  return g >= 150 && g <= 230;
}

/**
 * Count gold stars in the appraisal badge crop.
 *
 * The appraisal medallion shows 3 stars across a diagonal band. Each star is
 * gold when earned (R~255 G~200 B~80) or grey when not (R~180 G~180 B~180).
 * Stars are spaced roughly evenly horizontally despite the diagonal arrangement.
 *
 * Strategy: compute per-column gold-pixel density. Find local maxima above
 * threshold — each maximum is a star.
 *
 * Returns 0..3, or null when no medallion is detected.
 */
export function countAppraisalStarsFromRgba(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
): number | null {
  if (width < 30 || height < 15) return null;
  // Sanity: medallion has lots of warm-yellow/orange pixels. If almost all
  // pixels are white card, return null.
  let satPixels = 0;
  let pixels = 0;
  for (let i = 0; i < data.length; i += 4) {
    pixels++;
    const r = data[i], g = data[i + 1], b = data[i + 2];
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    if (max > 0 && (max - min) / max > 0.20) satPixels++;
  }
  if (pixels === 0 || satPixels / pixels < 0.10) return null;

  // Per-column gold density.
  const colDensity = new Array<number>(width).fill(0);
  for (let x = 0; x < width; x++) {
    let hits = 0;
    for (let y = 0; y < height; y++) {
      const i = (y * width + x) * 4;
      if (isAppraisalGold(data[i], data[i + 1], data[i + 2])) hits++;
    }
    colDensity[x] = hits / height;
  }
  // Find peak regions (>= 0.20 density). Group consecutive peak columns.
  const minDensity = 0.20;
  const minStarWidth = Math.max(4, Math.floor(width * 0.04));
  let stars = 0;
  let inPeak = false;
  let peakStart = 0;
  for (let x = 0; x < width; x++) {
    if (colDensity[x] >= minDensity) {
      if (!inPeak) {
        inPeak = true;
        peakStart = x;
      }
    } else {
      if (inPeak) {
        const w0 = x - peakStart;
        if (w0 >= minStarWidth) stars++;
        inPeak = false;
      }
    }
  }
  if (inPeak && (width - peakStart) >= minStarWidth) stars++;
  if (stars > 3) stars = 3;
  return stars;
}

// ── Box-cell shiny detector (used by inventoryOcr) ──────────────────────────
//
// Pokémon GO storage cells render shiny mons with a cluster of cyan-blue
// star particles overlaid on the sprite, plus a few bright cyan-white
// specular highlights from the sparkle animation. Detail-page sparkle is
// hard to read because the particles drift across the whole screen and
// often blend with the white background. In storage view the particles sit
// tightly inside the cell on a pale-green/cream backdrop, which gives a
// much cleaner pixel signal.
//
// The classifier counts two kinds of pixels inside a tight crop of the
// cell's SPRITE region (excluding CP banner and name strip):
//
//   dark cyan-blue stars  - R<130, B>130, B>R+25, B>G+10, G<B+30
//                           (B>G+10 excludes mint/teal UI elements)
//   bright cyan specular  - R>180, G>200, B>200, B>G+5, B-R in 10..50
//
// Calibrated against 6 real storage screenshots covering shiny, partial-
// shiny, shadow, IV-star filter, 9800-cap, and general (selected-cell)
// boxes. Trade-offs:
//   * Selected-cell halo (storage_general r0c0) reads as cyan and will
//     produce a single false positive per screenshot when the user has a
//     mon highlighted at capture time. Tolerated.
//   * Filter-bar overlap (storage_iv_stars r4) hits the teal UI dots and
//     will false-positive any cell that overlaps the filter bar. The
//     wider screen geometry already drops these rows in production.
//   * Some shiny cells (r3 of storage_full_shiny) sit below the visible
//     screenshot region and read 0% as expected.

export interface BoxCellShinyResult {
  isShiny: boolean;
  /** Density of dark cyan-blue "star" pixels, 0-1. */
  darkDensity: number;
  /** Density of bright cyan-white "specular" pixels, 0-1. */
  brightDensity: number;
}

export function boxCellShinyFromRgba(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
): BoxCellShinyResult {
  if (width < 4 || height < 4) {
    return { isShiny: false, darkDensity: 0, brightDensity: 0 };
  }
  let dark = 0;
  let bright = 0;
  let total = 0;
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    total++;
    // Dark cyan-blue star particle. The B>G+10 guard rules out mint/teal
    // UI accents (PoGo storage chrome) which would otherwise dominate.
    if (r < 130 && g < 200 && b > 130 && b > r + 25 && b > g + 10 && g < b + 30) {
      dark++;
    }
    // Bright cyan-white specular highlight from sparkle animation
    else if (
      r > 180 && g > 200 && b > 200 &&
      b > g + 5 &&
      b - r >= 10 && b - r <= 50
    ) {
      bright++;
    }
  }
  if (total === 0) return { isShiny: false, darkDensity: 0, brightDensity: 0 };
  const darkDensity = dark / total;
  const brightDensity = bright / total;
  // Two-signal classifier (calibrated to favour shiny SHOULD be clearly
  // above sprite noise, not a single stray pixel):
  //   - dark cluster alone qualifies at >= 0.30%
  //   - or weaker dark (>= 0.10%) plus visible bright sparkle (>= 0.50%)
  const isShiny =
    darkDensity >= 0.003 ||
    (darkDensity >= 0.001 && brightDensity >= 0.005);
  return { isShiny, darkDensity, brightDensity };
}

// ── BoxCell lucky detector ───────────────────────────────────────────────────
//
// Lucky Pokémon in PoGo carry a warm yellow/gold radial halo painted behind
// the sprite (and a yellow "LUCKY" sparkle ring). In the storage grid this
// presents as a saturated yellow background tint occupying a meaningful
// chunk of the cell's mid-band — fundamentally different from shiny
// (cyan particles, mostly transparent over the cell card) and from the
// favorite star (a small yellow-orange star glyph in the upper-right
// corner, ≤2% area).
//
// Strategy: count "warm yellow" pixels (R&G high, B notably lower, R≈G).
// Calibrated to:
//   * fire on the Lucky halo at >= ~3% density of the cell's middle band,
//   * NOT fire on the favorite star (small glyph, well under 1%),
//   * NOT fire on the yellow CP pill (sample band excludes the top 22% of
//     the cell where the pill lives — caller must crop accordingly),
//   * NOT confuse with the species-typing icons (those are saturated
//     primary colours occupying <0.5% of the sprite band).

export interface BoxCellLuckyResult {
  isLucky: boolean;
  /** Density of warm-yellow halo pixels, 0-1. */
  yellowDensity: number;
  /** Density of bright golden specular highlights, 0-1. */
  goldDensity: number;
}

export function boxCellLuckyFromRgba(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
): BoxCellLuckyResult {
  if (width < 4 || height < 4) {
    return { isLucky: false, yellowDensity: 0, goldDensity: 0 };
  }
  let yellow = 0;
  let gold = 0;
  let total = 0;
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    total++;
    // Warm yellow halo: R and G are both high and close, B is notably
    // lower. The (r-b) and (g-b) thresholds reject pale-grey cards
    // (r≈g≈b) and the cyan/teal shiny particles (b>g). The r-g <= 40
    // bound rejects red/orange pills (favorite star is narrower in
    // R-vs-G separation).
    if (
      r > 190 && g > 160 &&
      r - b >= 50 && g - b >= 30 &&
      Math.abs(r - g) <= 50 &&
      b < 180
    ) {
      yellow++;
    }
    // Golden specular highlight (LUCKY sparkle ring): near-white but
    // tilted warm. Catches the bright crescents around the halo.
    else if (
      r > 230 && g > 215 && b > 150 &&
      r >= g && g - b >= 25 && r - b >= 35 &&
      b < 220
    ) {
      gold++;
    }
  }
  if (total === 0) return { isLucky: false, yellowDensity: 0, goldDensity: 0 };
  const yellowDensity = yellow / total;
  const goldDensity = gold / total;
  // v1.2.1(13): thresholds lowered to register lucky on the build-12
  // trainer screenshots. The previous 4% / 1.8%+0.6% pair was tuned to
  // an older PoGo render where the lucky bubble bled across most of
  // the sprite background; in the current PoGo build the halo only
  // surrounds the sprite's silhouette, so the warm-yellow density per
  // cell tops out around 2.5-3.5%. Drops:
  //   - strong yellow alone: 4.0% → 2.5%
  //   - moderate yellow + gold sparkle: (1.8%, 0.6%) → (1.2%, 0.4%)
  // Favorite star alone still tops out near 0.5% yellow / 0.1% gold,
  // safely under the new gate. Shiny sparkles read cyan/teal (b>g) so
  // the per-pixel filter above already excludes them.
  const isLucky =
    yellowDensity >= 0.025 ||
    (yellowDensity >= 0.012 && goldDensity >= 0.004);
  return { isLucky, yellowDensity, goldDensity };
}

// ── TagBackground classifier (used by tagsOcr) ──────────────────────────────

export type TagBackground =
  | "sky_blue"
  | "yellow"
  | "dark"
  | "red"
  | "green"
  | "white"
  | "unknown";

export function classifyTagBackgroundFromRgba(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
): TagBackground {
  if (width < 1 || height < 1) return "unknown";
  let r = 0, g = 0, b = 0, n = 0;
  for (let i = 0; i < data.length; i += 4) {
    r += data[i];
    g += data[i + 1];
    b += data[i + 2];
    n++;
  }
  if (n === 0) return "unknown";
  r /= n; g /= n; b /= n;
  if (r < 110 && g < 110 && b < 110) return "dark";
  if (b > 200 && b - r > 15 && b - g > 5) return "sky_blue";
  if (r > 220 && g > 215 && b < 210) return "yellow";
  if (r > 230 && r - g > 25 && r - b > 25) return "red";
  if (g > 200 && g - r > 5 && g - b > 5) return "green";
  if (r > 235 && g > 235 && b > 235) return "white";
  return "unknown";
}

// ── Dex-cell 3-state geometry signals (PR #194 / b17b) ──────────────────────
//
// Build 208's caught classifier (the old `dexCellCaughtFromRgba`, vivid-pixel
// cluster detection) keyed "caught" on the PRESENCE of highly-saturated sprite
// pixels. That biased the decision against earth-tone species: brown / tan /
// cream / muted-pink sprites (Diglett, Dugtrio, Meowth, Cubone, Marowak,
// Slowpoke, Arcanine, the Pidgey line, even Charizard) carry little high-
// saturation chroma and read as uncaught, while yellow / red / blue / purple
// species pass. Misses did NOT correlate with sprite size — they correlated
// with saturation.
//
// PR #194 replaced it with independent, more permissive signals over a cell
// crop. b17o builds on the FIRST of those — the number-position centroid — as
// the ANCHOR of the rewritten classifier (see classifyDexCell in pokedexOcr.ts):
//
//   1. dexCellNumberPositionFromRgba — the vertical centroid (0-1) of the dex
//      number text within the cell. Pokémon GO renders the "#xxxx" label
//      VERTICALLY CENTRED for an unseen entry (blank frame) and pinned toward
//      the BOTTOM for a seen/caught entry (under the sprite). classifyNumber-
//      VerticalPosition turns this into a strict center/bottom/absent band.
//
//   2. dexCellChromaPresenceFromRgba — whether the sprite band carries any
//      meaningful colour at all (a PRESENCE test, not a saturation MEAN). Still
//      exported and exercised by the pixel tests; the b17o live-scan flow uses
//      analyzeSpriteHues instead, which separates a single-hue tinted SILHOUETTE
//      from a multi-hue CAUGHT sprite via circular hue variance.
//
// All are pure functions over an RGBA crop so they unit-test in Node (sharp →
// RGBA) and run verbatim in the browser (canvas getImageData). None touches the
// grid ROI geometry (GRID_BOTTOM / ROW_HEIGHT / HEADER_MAX_BOTTOM / NUM_ROWS),
// which stay locked in dexOcrRoi.ts.

/**
 * b17r R1: the variants whose dex-number digits keep the Main-dex rose-pink
 * palette. Their cell background is the warm peach (~246,190,177) and the
 * "#xxxx" label is the muted rose Pokémon GO prints over it, so the
 * hand-tuned rose formula (`isRosePinkNumberPixel`) — proven on device test
 * #1 — is the right fast path for them.
 *
 * b18d device test #3 narrowed this set: XXL and XXS were originally included
 * on the assumption they shared the Main-dex palette, but the Pokémon GO XXL
 * filter paints the card light-blue (~195,224,245) with BLUE digits
 * (~95,152,238) — blue dominant, NOT rose. The rose-pink predicate's
 * `r - g >= 70` floor rejects ~all of them, which produced `pos-absent` on
 * 422/471 cells in the IMG_9525 device log. XXL/XXS now route through the
 * dedicated `isXxlBlueNumberPixel` predicate below.
 */
const ROSE_PINK_NUMBER_VARIANTS: ReadonlySet<PokedexVariant> = new Set<PokedexVariant>([
  "pokemon",
  "shiny",
  "mega",
  "gmax",
]);

/**
 * b18d: the variants whose dex-number digits are rendered in BLUE on a cool
 * light-blue card. Currently XXL and XXS. Their digits are too saturated for
 * the adaptive (low-saturation) predicate, and they are blue — not rose — so
 * the Main-dex rose formula misses them too. They get their own predicate.
 */
const BLUE_NUMBER_VARIANTS: ReadonlySet<PokedexVariant> = new Set<PokedexVariant>([
  "xxl",
  "xxs",
]);

/**
 * True when (r,g,b) looks like a Main-dex rose dex-number glyph pixel — the
 * muted rose text Pokémon GO prints "#xxxx" in over the warm peach cell.
 * Hand-measured from the live fixtures: rose digits sit around (210,108,122) —
 * red clearly dominant over green, blue near/above green, mid-low luminance.
 * The orange/warm sprite pixels that could otherwise be confused have blue FAR
 * below green, so the `b >= g - 12` floor excludes them.
 */
function isRosePinkNumberPixel(r: number, g: number, b: number): boolean {
  const lum = 0.299 * r + 0.587 * g + 0.114 * b;
  return (
    r - g >= 70 &&
    r - g <= 135 &&
    b >= g - 12 &&
    b >= 95 &&
    b <= 160 &&
    lum < 190
  );
}

/**
 * b18d: true when (r,g,b) looks like an XXL/XXS dex-number glyph pixel — the
 * saturated blue text Pokémon GO prints "#xxxx" in over the cool light-blue
 * card. Hand-measured from IMG_9525: digits sit around (95,152,238) — blue
 * clearly dominant over green, green clearly above red, mid-low luminance.
 *
 * Sprite confounders are reasoned out as follows:
 *   - orange sprite (~245,150,40)        b - g ≈ -110  rejected
 *   - red Charmander          (~210,90,60) g - r ≈ -120  rejected
 *   - dark green Bulbasaur    (~60,130,80) b - g ≈ -50   rejected
 *   - cyan/teal               (~40,200,200) b - g ≈ 0     rejected (b - g floor)
 *   - light-blue cell BG      (~195,224,245) lum ≥ 215    rejected (lum cap)
 *   - white                   (~250,250,250) g - r = 0    rejected
 *
 * Purple sprites (e.g. Gengar ~130,70,200) could in principle clear the test,
 * but the number-anchor stage already restricts integration to the BOTTOM 50%
 * of the cell (see NUMBER_INTEGRATION_BOTTOM_FRAC), where the sprite never
 * reaches — sprites occupy the upper-middle of each cell by PoGo layout.
 */
function isXxlBlueNumberPixel(r: number, g: number, b: number): boolean {
  const lum = 0.299 * r + 0.587 * g + 0.114 * b;
  return (
    g - r >= 35 &&
    b - g >= 50 &&
    b >= 200 &&
    lum < 180
  );
}

/**
 * b17r R1: variant-agnostic dex-number glyph test for the cool/desaturated
 * themes (Perfect, Lucky, Purified, Shadow) whose digits are NOT rose-pink.
 *
 * On device test #2 (Perfect dex, 293 cells) the rose-pink rule above matched
 * almost nothing — 51% of cells reported "pos-absent" even though OCR had read
 * a digit in 97% of them — because Perfect renders its "#xxxx" label in a
 * pearl/silver/desaturated ink over a cool grey/blue background. Rather than
 * hand-tune a colour box per theme, this uses a palette-independent property
 * that holds across all of them: the digit ink is markedly DARKER than the
 * cell's own background AND/OR more saturated.
 *
 * The background reference is sampled by the caller from the cell corners
 * (where there is no glyph or sprite) and passed in as bgLum/bgSat, so the
 * test adapts to whatever theme the cell is drawn in. A pixel is a glyph when:
 *   - its luminance is clearly below the background luminance (dark ink), OR
 *   - its saturation is clearly above the background saturation (coloured ink
 *     such as Lucky's gold or Purified's teal on a near-grey field),
 * while never being brighter than the background (excludes specular sprite
 * highlights) and not fully background-coloured.
 */
function rgbLuminance(r: number, g: number, b: number): number {
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

function rgbSaturation(r: number, g: number, b: number): number {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  return max <= 0 ? 0 : (max - min) / max;
}

const ADAPTIVE_DARK_LUM_DELTA = 45; // glyph ink at least this much darker than bg
// Dex-number ink on the cool/desaturated themes is a near-neutral
// pearl/silver/grey, not a strongly chromatic colour. A pixel that is both dark
// AND highly saturated is a sprite (which can sit darker than the cell
// background), not digit ink. Capping the saturation of an accepted "dark ink"
// pixel keeps a coloured sprite from being counted as a number glyph, which
// previously dragged the number centroid up out of the bottom band and made
// caught cells read "center"/never-seen.
const ADAPTIVE_DARK_MAX_SAT = 0.45;

function isAdaptiveNumberPixel(
  r: number,
  g: number,
  b: number,
  bgLum: number,
  _bgSat: number,
): boolean {
  // The rose-pink "#NNNN" label is the shared dex-number colour and also appears
  // on these themes, so accept it directly before the palette-independent
  // fallback.
  if (isRosePinkNumberPixel(r, g, b)) return true;
  const lum = rgbLuminance(r, g, b);
  const sat = rgbSaturation(r, g, b);
  // Near-neutral ink that is clearly darker than the cell's own background.
  return lum <= bgLum - ADAPTIVE_DARK_LUM_DELTA && sat <= ADAPTIVE_DARK_MAX_SAT;
}

/**
 * b17r R1 / b18d: variant-aware dex-number glyph classifier.
 *
 * For the rose-pink variants it delegates to the proven Main-dex formula. For
 * XXL/XXS it delegates to the dedicated blue-glyph formula (b18d). For the
 * cool/desaturated themes it uses the adaptive (background-relative) heuristic,
 * which needs the cell's background luminance/saturation reference — those
 * default to the Main-dex peach when omitted so chroma callers and the unit
 * tests keep working unchanged.
 */
function isDexNumberPixel(
  r: number,
  g: number,
  b: number,
  variant: PokedexVariant = "pokemon",
  bgLum = rgbLuminance(POGO_BG_R, POGO_BG_G, POGO_BG_B),
  bgSat = rgbSaturation(POGO_BG_R, POGO_BG_G, POGO_BG_B),
): boolean {
  if (ROSE_PINK_NUMBER_VARIANTS.has(variant)) {
    return isRosePinkNumberPixel(r, g, b);
  }
  if (BLUE_NUMBER_VARIANTS.has(variant)) {
    return isXxlBlueNumberPixel(r, g, b);
  }
  return isAdaptiveNumberPixel(r, g, b, bgLum, bgSat);
}

export interface DexNumberPosition {
  /** Vertical centroid of the number glyphs, as a fraction of cell height (0-1). */
  centroid: number;
  /** Fraction of cell pixels classified as number glyphs (0-1). */
  density: number;
  /** True when enough number pixels were found to trust `centroid`. */
  found: boolean;
}

/** Below this glyph density we cannot locate a number and report found:false. */
const NUMBER_MIN_DENSITY = 0.002;

/**
 * b17v Fix 1: the number-position centroid integrates glyph pixels over the
 * BOTTOM band of the cell only, starting at this fraction of cell height.
 *
 * Why: the centroid used to integrate over the WHOLE cell. On the perfect/lucky
 * themes a caught entry's sprite fills the upper ~70% of the cell, and those
 * sprite pixels satisfy `isDexNumberPixel`, so they dragged the whole-cell
 * centroid UP — a genuinely bottom-pinned (caught) number then read centroid
 * < 0.55 and was misclassified "center" (never-seen). The device log showed
 * 147 numbers read but only 13 applied because of exactly this collapse.
 *
 * Restricting integration to the bottom 50% excludes the sprite entirely:
 *   - CAUGHT cell: number pinned to the bottom → most pixels near the bottom of
 *     the band → normalised centroid high (→ "bottom" via the 0.55 split).
 *   - UNCAUGHT / never-seen cell: number vertically centred in the cell → its
 *     glyph pixels sit near the TOP of the bottom band → normalised centroid low
 *     (→ "center").
 * The reported centroid is normalised to [0,1] OVER THE BAND (0 = band top,
 * 1 = band bottom), so `classifyNumberVerticalPosition`'s 0.55 split is unchanged.
 */
const NUMBER_INTEGRATION_BOTTOM_FRAC = 0.5;

/**
 * b17r R1: estimate a cell's background luminance/saturation from its four
 * corners. The dex number sits centred-or-bottom and the sprite sits in the
 * upper-middle, so the corner patches are reliably background field across
 * every theme. Returns the median luminance and saturation over the sampled
 * corner pixels so a stray sprite/glyph pixel in one corner cannot skew the
 * reference.
 */
function sampleCornerBackground(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
): { lum: number; sat: number } {
  const patch = Math.max(1, Math.round(Math.min(width, height) * 0.12));
  const lums: number[] = [];
  const sats: number[] = [];
  const corners: Array<[number, number]> = [
    [0, 0],
    [width - patch, 0],
    [0, height - patch],
    [width - patch, height - patch],
  ];
  for (const [cx, cy] of corners) {
    for (let y = cy; y < Math.min(height, cy + patch); y++) {
      for (let x = cx; x < Math.min(width, cx + patch); x++) {
        const i = (y * width + x) * 4;
        lums.push(rgbLuminance(data[i], data[i + 1], data[i + 2]));
        sats.push(rgbSaturation(data[i], data[i + 1], data[i + 2]));
      }
    }
  }
  if (lums.length === 0) {
    return {
      lum: rgbLuminance(POGO_BG_R, POGO_BG_G, POGO_BG_B),
      sat: rgbSaturation(POGO_BG_R, POGO_BG_G, POGO_BG_B),
    };
  }
  const median = (xs: number[]): number => {
    const sorted = [...xs].sort((a, b) => a - b);
    const mid = sorted.length >> 1;
    return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
  };
  return { lum: median(lums), sat: median(sats) };
}

/**
 * Locate the dex number within a cell crop and report its vertical centroid as a
 * fraction of cell height (0 = top, 1 = bottom). The caller uses a low centroid
 * (number vertically centred) as the "unseen" cue and a high one (number pinned
 * to the bottom under a sprite) as the "seen/caught" cue.
 *
 * Pure function over an RGBA buffer. Returns found:false when too few glyph
 * pixels are present to trust the centroid.
 */
export function dexCellNumberPositionFromRgba(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  variant: PokedexVariant = "pokemon",
  pillMask: PillRegion | null = null,
): DexNumberPosition {
  if (width < 2 || height < 2) {
    return { centroid: 0, density: 0, found: false };
  }
  // b17r R1 / b18d: for the adaptive cool/desaturated themes the glyph test is
  // relative to the cell's own background, so sample a background reference
  // from the four corners (away from the centred sprite and the number band)
  // and feed its luminance/saturation into the adaptive predicate. The
  // rose-pink and XXL/XXS blue variants ignore these (their formulas are
  // absolute), so we only pay the sampling cost for the variants that need it.
  let bgLum = rgbLuminance(POGO_BG_R, POGO_BG_G, POGO_BG_B);
  let bgSat = rgbSaturation(POGO_BG_R, POGO_BG_G, POGO_BG_B);
  if (
    !ROSE_PINK_NUMBER_VARIANTS.has(variant) &&
    !BLUE_NUMBER_VARIANTS.has(variant)
  ) {
    const ref = sampleCornerBackground(data, width, height);
    bgLum = ref.lum;
    bgSat = ref.sat;
  }
  // b17v Fix 1: integrate glyph pixels only over the bottom band so sprite
  // pixels above the number can never skew the centroid.
  const bandTop = Math.min(height - 1, Math.floor(height * NUMBER_INTEGRATION_BOTTOM_FRAC));
  const bandHeight = height - bandTop;
  let count = 0;
  let weightedY = 0;
  for (let y = bandTop; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (pillMask !== null && pixelInPill(x, y, pillMask)) continue;
      const i = (y * width + x) * 4;
      if (isDexNumberPixel(data[i], data[i + 1], data[i + 2], variant, bgLum, bgSat)) {
        count++;
        weightedY += y;
      }
    }
  }
  // Density is over the integration band, keeping NUMBER_MIN_DENSITY meaningful
  // now that the denominator no longer includes the (excluded) sprite region.
  const total = width * bandHeight;
  const density = total > 0 ? count / total : 0;
  if (count === 0 || density < NUMBER_MIN_DENSITY) {
    return { centroid: 0, density, found: false };
  }
  // Normalise the centroid to [0,1] within the band (0 = band top, 1 = band
  // bottom) so the downstream 0.55 split is unchanged by the new window.
  const centroid = bandHeight > 1 ? (weightedY / count - bandTop) / (bandHeight - 1) : 0;
  return { centroid, density, found: true };
}

export interface DexChromaPresence {
  present: boolean;
  /** Fraction of sampled pixels that read as coloured (non-background) sprite. */
  density: number;
}

/**
 * Lower bound on sprite-pixel density for a sprite to count as present. Tuned
 * against the live Pokémon GO fixtures (IMG_9162 rows 089–104): empty pink cells
 * produce a sprite-pixel density near 0 (≤0.011), while every caught sprite —
 * including the muted earth-tones Build 208 missed (Voltorb 100, Exeggutor 103,
 * Marowak 104) — clears 0.018. 0.015 sits in that gap with margin on both sides.
 * Kept low on purpose: the whole point of PR #194 is to stop biasing against
 * low-saturation sprites.
 */
export const SPRITE_CHROMA_MIN_DENSITY = 0.015;

/**
 * Pokémon GO's uniform empty-cell background — a bright pink/peach. Measured
 * across the live fixtures (IMG_9162 etc.) at ~(246,190,177). The chroma test is
 * a DISTANCE-from-this-background test, not a saturation test, because earth-tone
 * sprites (brown/tan/cream) are only weakly saturated yet still sit far from this
 * specific pink — that distinction is exactly what Build 208's saturation gate
 * could not make.
 */
const POGO_BG_R = 246;
const POGO_BG_G = 190;
const POGO_BG_B = 177;
/**
 * A pixel this far (Euclidean RGB) from the pink background is sprite, not
 * background. 60 cleanly clears JPEG noise / anti-aliasing around the empty-cell
 * pink (which stays under ~45) while admitting muted earth-tone sprite pixels.
 */
const CHROMA_BG_DISTANCE = 60;

/**
 * Decide whether a cell carries a coloured sprite, as opposed to Pokémon GO's
 * uniform pink/peach empty-cell background.
 *
 * A pixel counts as sprite when its Euclidean RGB distance from the measured
 * background pink exceeds `CHROMA_BG_DISTANCE` — this captures sprite pixels of
 * ANY hue, including the muted earth-tones (brown/tan/cream) that a saturation
 * gate misses, while the near-uniform pink background contributes ~0. Dex-number
 * glyphs are excluded so the centred number of an UNSEEN cell does not
 * masquerade as a sprite.
 *
 * Pure function over an RGBA buffer.
 */
export function dexCellChromaPresenceFromRgba(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
): DexChromaPresence {
  if (width < 2 || height < 2) {
    return { present: false, density: 0 };
  }
  let total = 0;
  let coloured = 0;
  const distSq = CHROMA_BG_DISTANCE * CHROMA_BG_DISTANCE;
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i], g = data[i + 1], b = data[i + 2];
    if (isDexNumberPixel(r, g, b)) continue;
    total++;
    const dr = r - POGO_BG_R, dg = g - POGO_BG_G, db = b - POGO_BG_B;
    if (dr * dr + dg * dg + db * db > distSq) {
      coloured++;
    }
  }
  const density = total > 0 ? coloured / total : 0;
  return { present: density >= SPRITE_CHROMA_MIN_DENSITY, density };
}

/* ────────────────────────────────────────────────────────────────────────────
   b17o: number-anchor-first cell classifier primitives.

   The rewritten live-scan classifier reads the dex number FIRST (the anchor),
   classifies its vertical position, and only then inspects the sprite. Two new
   pure helpers back that flow:

     1. classifyNumberVerticalPosition — turn a DexNumberPosition centroid into a
        strict "center" | "bottom" | "absent" band. Center means the number is
        vertically centred in a blank frame (NEVER-SEEN); bottom means it is
        pinned under the sprite area (proceed to the sprite test). The middle
        band is treated as "absent" (skip the cell) — a deliberate strict choice
        over a tolerance fallback so an ambiguous read never invents a state.

     2. analyzeSpriteHues — decide whether a sprite region is an EMPTY cell, a
        single-hue SILHOUETTE, or a multi-hue CAUGHT sprite. Pokémon GO tints the
        silhouette with the active dex's theme colour (gold for Shiny, pink for
        Mega/G-Max, purple for Shadow, teal for Purified, grey for Main), so a
        saturation gate cannot separate silhouette from sprite. Hue VARIANCE can:
        a silhouette is one dominant hue (low circular variance) with real
        luminance structure; a caught sprite spans many hues (high variance).

   Both are DOM-free and operate on an already-cropped RGBA buffer, matching the
   *FromRgba convention the rest of this module and its Node test harness use.
   ──────────────────────────────────────────────────────────────────────── */

export type NumberVerticalPosition = "center" | "bottom" | "absent";

export interface NumberPositionThresholds {
  /**
   * Single split point on centroid/height. centroid below this → "center"
   * (number centred in a blank frame, NEVER-SEEN); at or above → "bottom"
   * (number pinned under the sprite, at least SEEN).
   */
  splitAt?: number;
}

/**
 * b17p A3: the centroid here is computed over the WHOLE cell (weightedY / count
 * / height in dexCellNumberPositionFromRgba), not a digit-only ROI, so the old
 * 0.45/0.65 dead band threw away real signal: ~45% of cells with a clean dex
 * number landed in 0.45..0.65 and were dropped as "no-canvas"/absent. A single
 * split at 0.55 partitions the space with no dead band — a found number is
 * always either center or bottom.
 */
const DEFAULT_NUMBER_SPLIT = 0.55;

/**
 * Vertical-position band for the dex number. Returns "absent" only when the
 * number was not found; otherwise a single split decides center vs bottom with
 * no ambiguous dead band. Pure.
 */
export function classifyNumberVerticalPosition(
  pos: DexNumberPosition,
  opts: NumberPositionThresholds = {},
): NumberVerticalPosition {
  if (!pos.found) return "absent";
  const splitAt = opts.splitAt ?? DEFAULT_NUMBER_SPLIT;
  return pos.centroid < splitAt ? "center" : "bottom";
}

export interface HueAnalysis {
  /** Mean hue of foreground pixels, 0-360. NaN when no foreground. */
  dominantHue: number;
  /** Circular hue variance over foreground pixels, 0-1. Low = single hue. */
  hueVariance: number;
  /** Normalised luminance variance over foreground pixels, 0-1. */
  luminanceVariance: number;
  /** Count of foreground (non-background) pixels. */
  foregroundPixels: number;
  /** Low hue variance + meaningful luminance variance + enough foreground. */
  isSilhouette: boolean;
  /** Foreground pixel count below the empty threshold. */
  isEmpty: boolean;
}

export interface SpriteHueOptions {
  /**
   * How far (0-1, fraction of full luminance range) a pixel's luminance must sit
   * from the sampled background luminance to count as foreground. Dynamic floor;
   * the background is estimated from the crop's corners.
   */
  foregroundLuminanceThreshold?: number;
  /** Foreground fraction (0-1) of the region below which the cell is "empty". */
  emptyPixelThreshold?: number;
  /** Circular hue variance below which a non-empty region is a silhouette. */
  silhouetteHueVarianceThreshold?: number;
  /** Min luminance variance for a silhouette (rules out a flat colour wash). */
  silhouetteMinLuminanceVariance?: number;
}

const DEFAULT_FG_LUM_THRESHOLD = 0.16;
const DEFAULT_EMPTY_PIXEL_THRESHOLD = 0.05;
const DEFAULT_SILHOUETTE_HUE_VAR_THRESHOLD = 0.15;
const DEFAULT_SILHOUETTE_MIN_LUM_VAR = 0.0008;

/**
 * Round-9 Fix A — purified-only empty-cell floor.
 *
 * Purified (mint-aura) sprites render LOW-CONTRAST: the washed-out body sits
 * close to the page background in luminance, so a real catch clears far fewer
 * foreground pixels than a normal sprite. The device purified scan logged 152
 * `spriteState:"empty"` + 61 `"none"` against only 56 `"caught"` over 231 cell
 * classifies — real catches were being auto-classified `empty` by the default
 * 5%-coverage floor and never reached the confirmation pipeline.
 *
 * For `variant === "purified"` ONLY, drop the empty-coverage requirement to half
 * the default (`DEFAULT_EMPTY_PIXEL_THRESHOLD` 0.05 → 0.025) so a thin washed-out
 * sprite is no longer mislabeled empty. It is NOT zeroed: a non-zero floor must
 * remain so a genuinely blank purified cell still reads empty. All other variants
 * keep the 0.05 default unchanged.
 */
export const PURIFIED_FOREGROUND_PIXEL_FLOOR = 0.025;

function luminance01(r: number, g: number, b: number): number {
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
}

function pixelHueDeg(r: number, g: number, b: number): number | null {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
  const d = max - min;
  // Near-achromatic pixels carry no reliable hue; a grey silhouette is exactly
  // this case, so we still count them as foreground (luminance) but skip them
  // from the hue accumulation, which keeps a grey sprite at very low hue
  // variance (its few chromatic pixels cluster) rather than random noise.
  if (d < 0.04) return null;
  let h: number;
  if (max === rn) h = ((gn - bn) / d) % 6;
  else if (max === gn) h = (bn - rn) / d + 2;
  else h = (rn - gn) / d + 4;
  h *= 60;
  if (h < 0) h += 360;
  return h;
}

/**
 * Estimate the cell-background luminance by averaging the four corner regions of
 * the crop. The dex card paints a tinted background that varies per variant, so
 * we sample locally rather than assume a fixed value.
 */
function estimateBackgroundLuminance(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
): number {
  const sw = Math.max(1, Math.floor(width * 0.18));
  const sh = Math.max(1, Math.floor(height * 0.18));
  const corners: Array<[number, number]> = [
    [0, 0],
    [width - sw, 0],
    [0, height - sh],
    [width - sw, height - sh],
  ];
  let sum = 0;
  let n = 0;
  for (const [cx, cy] of corners) {
    for (let y = cy; y < cy + sh; y++) {
      for (let x = cx; x < cx + sw; x++) {
        const i = (y * width + x) * 4;
        sum += luminance01(data[i], data[i + 1], data[i + 2]);
        n++;
      }
    }
  }
  return n > 0 ? sum / n : 0;
}

/**
 * b18f: estimate the cell-background COLOUR (not just luminance) by averaging the
 * four corner regions, using the same corner geometry as
 * estimateBackgroundLuminance. The no-silhouette chroma test measures each
 * pixel's distance from this colour, so it adapts to whatever tint the page is
 * drawn in (pink Main, light-blue XXL, etc.).
 */
export function estimateBackgroundRgb(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
): { r: number; g: number; b: number } {
  const sw = Math.max(1, Math.floor(width * 0.18));
  const sh = Math.max(1, Math.floor(height * 0.18));
  const corners: Array<[number, number]> = [
    [0, 0],
    [width - sw, 0],
    [0, height - sh],
    [width - sw, height - sh],
  ];
  let sr = 0, sg = 0, sb = 0, n = 0;
  for (const [cx, cy] of corners) {
    for (let y = cy; y < cy + sh; y++) {
      for (let x = cx; x < cx + sw; x++) {
        const i = (y * width + x) * 4;
        sr += data[i];
        sg += data[i + 1];
        sb += data[i + 2];
        n++;
      }
    }
  }
  return n > 0 ? { r: sr / n, g: sg / n, b: sb / n } : { r: 0, g: 0, b: 0 };
}

/**
 * Classify a sprite-region RGBA crop as empty, single-hue silhouette, or
 * multi-hue caught sprite via circular hue variance.
 *
 * Circular variance: each foreground chromatic pixel's hue becomes a unit vector
 * (cos h, sin h); the magnitude R of the averaged vector gives variance = 1 - R.
 * Hues clustered around one value → R near 1 → variance near 0 (silhouette).
 * Hues spread around the wheel → R near 0 → variance near 1 (caught).
 *
 * Pure function over an RGBA buffer (the sprite region, already cropped).
 */
export function analyzeSpriteHues(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  opts: SpriteHueOptions = {},
  pillMask: PillRegion | null = null,
): HueAnalysis {
  const empty: HueAnalysis = {
    dominantHue: NaN,
    hueVariance: 0,
    luminanceVariance: 0,
    foregroundPixels: 0,
    isSilhouette: false,
    isEmpty: true,
  };
  if (width < 2 || height < 2) return empty;

  const fgLumThreshold = opts.foregroundLuminanceThreshold ?? DEFAULT_FG_LUM_THRESHOLD;
  const emptyThreshold = opts.emptyPixelThreshold ?? DEFAULT_EMPTY_PIXEL_THRESHOLD;
  const hueVarThreshold = opts.silhouetteHueVarianceThreshold ?? DEFAULT_SILHOUETTE_HUE_VAR_THRESHOLD;
  const minLumVar = opts.silhouetteMinLuminanceVariance ?? DEFAULT_SILHOUETTE_MIN_LUM_VAR;

  const bgLum = estimateBackgroundLuminance(data, width, height);

  let fg = 0;
  let sx = 0, sy = 0, chroma = 0;
  let lumSum = 0, lumSqSum = 0;
  const totalPixels = width * height;
  for (let py = 0; py < height; py++) {
    for (let px = 0; px < width; px++) {
      if (pillMask !== null && pixelInPill(px, py, pillMask)) continue;
      const i = (py * width + px) * 4;
      const r = data[i], g = data[i + 1], b = data[i + 2];
      const lum = luminance01(r, g, b);
      if (Math.abs(lum - bgLum) < fgLumThreshold) continue; // background
      fg++;
      lumSum += lum;
      lumSqSum += lum * lum;
      const h = pixelHueDeg(r, g, b);
      if (h != null) {
        const rad = (h * Math.PI) / 180;
        sx += Math.cos(rad);
        sy += Math.sin(rad);
        chroma++;
      }
    }
  }

  const foregroundFraction = totalPixels > 0 ? fg / totalPixels : 0;
  if (foregroundFraction < emptyThreshold) {
    return { ...empty, foregroundPixels: fg };
  }

  const lumMean = fg > 0 ? lumSum / fg : 0;
  const luminanceVariance = fg > 0 ? Math.max(0, lumSqSum / fg - lumMean * lumMean) : 0;

  // Circular hue variance over the chromatic foreground pixels. When a sprite is
  // a near-pure grey silhouette there are almost no chromatic pixels; treat that
  // as fully-clustered (variance 0) since there is no hue spread to speak of.
  let dominantHue = NaN;
  let hueVariance = 0;
  if (chroma > 0) {
    const R = Math.sqrt(sx * sx + sy * sy) / chroma;
    hueVariance = Math.max(0, Math.min(1, 1 - R));
    let mean = (Math.atan2(sy, sx) * 180) / Math.PI;
    if (mean < 0) mean += 360;
    dominantHue = mean;
  }

  const isSilhouette =
    hueVariance < hueVarThreshold && luminanceVariance >= minLumVar;

  return {
    dominantHue,
    hueVariance,
    luminanceVariance,
    foregroundPixels: fg,
    isSilhouette,
    isEmpty: false,
  };
}

/**
 * b18f: background-adaptive chroma emptiness test for the no-silhouette variants
 * (XXL, XXS, Lucky, Perfect).
 *
 * Why this exists alongside analyzeSpriteHues: that function decides emptiness on
 * a LUMINANCE distance from the corner-sampled background. On Pokemon GO's XXL
 * Dex the whole page is a light-blue gradient (~195,224,245) and small or
 * medium-light sprites (Caterpie green, Pidgey/Pidgeotto tan, Eevee cream,
 * Squirtle blue) sit close to that background in luminance, so most of their
 * pixels fall under the +-0.16 lum band and the cell reads empty even though a
 * sprite is plainly there. b18d device test #3 saw 25 such false negatives.
 *
 * Chroma distance does not have that failure: a green/brown/cream sprite sits FAR
 * from the blue page background in full RGB even when their luminances are close,
 * while the near-uniform background contributes a distance near zero. We sample
 * the background colour from the same four corners analyzeSpriteHues uses (so the
 * test adapts to whatever tint the page is drawn in rather than assuming pink or
 * blue) and count pixels whose Euclidean RGB distance from it exceeds
 * CHROMA_BG_DISTANCE. Dex-number glyph pixels for the variant are excluded so a
 * centred number on an unseen cell cannot masquerade as a sprite.
 *
 * This is intentionally a SECOND opinion: classifyCellSprite only overrides to
 * empty when BOTH this and analyzeSpriteHues agree, so the chroma path can never
 * turn a luminance-detected sprite into an empty.
 *
 * Pure function over an RGBA crop (the sprite region, already cropped).
 */
export interface SpriteChromaAnalysis {
  /** Count of pixels whose RGB distance from the sampled background is large. */
  chromaForegroundPixels: number;
  /** Fraction of region pixels that read as chroma foreground (0-1). */
  chromaFraction: number;
  /** True when the chroma fraction is below the empty threshold. */
  isEmpty: boolean;
}

export interface SpriteChromaOptions {
  /** Euclidean RGB distance from the sampled background above which a pixel is sprite. */
  chromaBgDistance?: number;
  /** Chroma fraction (0-1) of the region below which the cell is "empty". */
  chromaEmptyFraction?: number;
  /** Variant whose dex-number glyph pixels are excluded from the count. */
  variant?: PokedexVariant;
}

/**
 * Distance (Euclidean RGB) from the sampled cell background above which a pixel
 * counts as sprite. Matches the proven CHROMA_BG_DISTANCE used by the pink-bg
 * presence test: it clears JPEG noise and anti-aliasing around a flat background
 * (which stays under ~45) while admitting light-but-coloured sprite pixels.
 */
const DEFAULT_CHROMA_BG_DISTANCE = 60;
/**
 * Chroma fraction below which a no-silhouette cell reads empty. Kept low (sprites
 * cover only part of the cell and edges are anti-aliased toward the background)
 * and aligned with SPRITE_CHROMA_MIN_DENSITY so the two chroma paths agree.
 */
const DEFAULT_CHROMA_EMPTY_FRACTION = 0.015;

export function analyzeSpriteChroma(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  opts: SpriteChromaOptions = {},
  pillMask: PillRegion | null = null,
): SpriteChromaAnalysis {
  if (width < 2 || height < 2) {
    return { chromaForegroundPixels: 0, chromaFraction: 0, isEmpty: true };
  }
  const bgDistance = opts.chromaBgDistance ?? DEFAULT_CHROMA_BG_DISTANCE;
  const emptyFraction = opts.chromaEmptyFraction ?? DEFAULT_CHROMA_EMPTY_FRACTION;
  const variant = opts.variant ?? "pokemon";

  const bg = estimateBackgroundRgb(data, width, height);
  const distSq = bgDistance * bgDistance;

  let total = 0;
  let coloured = 0;
  for (let py = 0; py < height; py++) {
    for (let px = 0; px < width; px++) {
      if (pillMask !== null && pixelInPill(px, py, pillMask)) continue;
      const i = (py * width + px) * 4;
      const r = data[i], g = data[i + 1], b = data[i + 2];
      if (isDexNumberPixel(r, g, b, variant)) continue;
      total++;
      const dr = r - bg.r, dg = g - bg.g, db = b - bg.b;
      if (dr * dr + dg * dg + db * db > distSq) coloured++;
    }
  }
  const chromaFraction = total > 0 ? coloured / total : 0;
  return {
    chromaForegroundPixels: coloured,
    chromaFraction,
    isEmpty: chromaFraction < emptyFraction,
  };
}

/* ────────────────────────────────────────────────────────────────────────────
   PR-E18 #4: local-cell-background-relative sprite presence.

   The global POGO_BG=(246,190,177) constant is a warm near-white. It works on
   the Main dex but fails on the deep-dex REGION PAGES, which paint a strongly
   coloured full-bleed background that changes per dex (light-blue XXL, coral
   Perfect, lavender Shadow, mint Purified, pink G-Max). On those pages a faint
   pale sprite has LOWER chroma-vs-POGO_BG than the saturated empty background,
   so a global chroma threshold ranks the caught cell as LESS present than the
   empty one (measured: XXL 0856 caught chroma_max 0.247 < empty 0852 0.580).

   The fix is to measure presence RELATIVE TO THIS CELL'S OWN BACKGROUND, which
   is reliably sampled from the corner/edge ring (background field in both
   caught and empty cells). Two background-independent presence cues:

     (a) EDGE DENSITY over the cell interior. A sprite has internal edges
         (silhouette boundary, shading); a flat background does not. A Sobel
         gradient magnitude is independent of the absolute background colour, so
         it is the strongest presence cue on a coloured page.

     (b) LOCAL DEVIATION fraction: the fraction of interior pixels whose colour
         distance from the locally-sampled cellBg exceeds a small delta. A faint
         pale sprite still differs from its OWN background in localized patches
         even when its global chroma is low.

   The histogram/fingerprint match stays a SECONDARY confirmer in
   cellSpriteMatcher, not the presence gate.
   ──────────────────────────────────────────────────────────────────────── */

/**
 * Estimate a cell's own background colour from its corner patches plus the
 * outer edge ring. The dex number sits centred-or-bottom and the sprite sits in
 * the upper-middle, so the outer ring is reliably background field across every
 * theme. The median per channel rejects a stray sprite/glyph pixel that pokes
 * into a corner.
 */
export function estimateLocalCellBackground(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
): { r: number; g: number; b: number } {
  if (width < 2 || height < 2) return { r: 0, g: 0, b: 0 };
  const ring = Math.max(1, Math.round(Math.min(width, height) * 0.1));
  const rs: number[] = [];
  const gs: number[] = [];
  const bs: number[] = [];
  for (let y = 0; y < height; y++) {
    const topOrBottom = y < ring || y >= height - ring;
    for (let x = 0; x < width; x++) {
      const leftOrRight = x < ring || x >= width - ring;
      if (!topOrBottom && !leftOrRight) continue; // interior, skip
      const i = (y * width + x) * 4;
      rs.push(data[i]);
      gs.push(data[i + 1]);
      bs.push(data[i + 2]);
    }
  }
  if (rs.length === 0) return { r: 0, g: 0, b: 0 };
  const median = (xs: number[]): number => {
    const sorted = [...xs].sort((a, b) => a - b);
    const mid = sorted.length >> 1;
    return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
  };
  return { r: median(rs), g: median(gs), b: median(bs) };
}

/**
 * Sobel gradient-magnitude density over the cell INTERIOR (the outer ring is
 * skipped so the cell border does not register as an edge). A pixel counts as
 * an edge when its luminance gradient magnitude exceeds `threshold` (0-255
 * scale). Returns the fraction of interior pixels that are edges, 0-1.
 *
 * Edge density is the most background-colour-independent presence cue: a flat
 * background of ANY colour has near-zero internal gradient, while a sprite of
 * any palette carries internal edges from its silhouette boundary and shading.
 */
export function edgeDensityFromRgba(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  threshold = 36,
): number {
  if (width < 4 || height < 4) return 0;
  const lum = (x: number, y: number): number => {
    const i = (y * width + x) * 4;
    return 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
  };
  const inset = Math.max(1, Math.round(Math.min(width, height) * 0.1));
  let edges = 0;
  let total = 0;
  for (let y = inset; y < height - inset; y++) {
    for (let x = inset; x < width - inset; x++) {
      const gx =
        -lum(x - 1, y - 1) - 2 * lum(x - 1, y) - lum(x - 1, y + 1) +
        lum(x + 1, y - 1) + 2 * lum(x + 1, y) + lum(x + 1, y + 1);
      const gy =
        -lum(x - 1, y - 1) - 2 * lum(x, y - 1) - lum(x + 1, y - 1) +
        lum(x - 1, y + 1) + 2 * lum(x, y + 1) + lum(x + 1, y + 1);
      const mag = Math.sqrt(gx * gx + gy * gy);
      total++;
      if (mag >= threshold) edges++;
    }
  }
  return total > 0 ? edges / total : 0;
}

export interface LocalPresenceResult {
  /** True when the cell carries a sprite distinct from its own background. */
  present: boolean;
  /** Edge-density signal over the interior (0-1). */
  edgeDensity: number;
  /** Fraction of interior pixels deviating from the local cell background (0-1). */
  deviationFraction: number;
  /** Combined presence confidence (0-1). */
  confidence: number;
}

export interface LocalPresenceOptions {
  /** Min interior edge density to count a sprite as present on its own. */
  edgeDensityThreshold?: number;
  /** RGB distance from the local cell background above which a pixel deviates. */
  bgDeviationDistance?: number;
  /** Min interior deviation fraction to count a sprite as present on its own. */
  deviationFractionThreshold?: number;
}

/**
 * Default edge density above which a cell is considered to carry a sprite. A
 * faint pale sprite on a coloured page still produces internal edges well above
 * a flat background (which sits near 0 after the ring is excluded). Kept low so
 * small/pale deep-dex sprites are recovered, with margin over JPEG noise.
 */
const DEFAULT_EDGE_DENSITY_THRESHOLD = 0.02;
/** RGB distance from the LOCAL cell background above which a pixel deviates. */
const DEFAULT_BG_DEVIATION_DISTANCE = 40;
/** Default interior deviation fraction above which a cell carries a sprite. */
const DEFAULT_DEVIATION_FRACTION_THRESHOLD = 0.05;

/**
 * Background-colour-independent sprite presence over a cell crop. Combines (a)
 * interior edge density and (b) fraction of interior pixels deviating from the
 * locally-sampled cell background. A cell is PRESENT when either cue clears its
 * threshold, so a faint pale sprite that is weak on one cue but clear on the
 * other is still recovered. A flat coloured background fails both (no internal
 * edges, no interior deviation from its own colour) and reads MISSING.
 *
 * Pure function over an RGBA crop (the sprite region, already cropped).
 */
export function localBgPresenceFromRgba(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  opts: LocalPresenceOptions = {},
): LocalPresenceResult {
  if (width < 4 || height < 4) {
    return { present: false, edgeDensity: 0, deviationFraction: 0, confidence: 0 };
  }
  const edgeThreshold = opts.edgeDensityThreshold ?? DEFAULT_EDGE_DENSITY_THRESHOLD;
  const devDistance = opts.bgDeviationDistance ?? DEFAULT_BG_DEVIATION_DISTANCE;
  const devFractionThreshold =
    opts.deviationFractionThreshold ?? DEFAULT_DEVIATION_FRACTION_THRESHOLD;

  const bg = estimateLocalCellBackground(data, width, height);
  const edgeDensity = edgeDensityFromRgba(data, width, height);

  const inset = Math.max(1, Math.round(Math.min(width, height) * 0.1));
  const distSq = devDistance * devDistance;
  let deviating = 0;
  let total = 0;
  for (let y = inset; y < height - inset; y++) {
    for (let x = inset; x < width - inset; x++) {
      const i = (y * width + x) * 4;
      const dr = data[i] - bg.r;
      const dg = data[i + 1] - bg.g;
      const db = data[i + 2] - bg.b;
      total++;
      if (dr * dr + dg * dg + db * db > distSq) deviating++;
    }
  }
  const deviationFraction = total > 0 ? deviating / total : 0;

  const edgeScore = Math.min(1, edgeDensity / (edgeThreshold * 4));
  const devScore = Math.min(1, deviationFraction / (devFractionThreshold * 4));
  const confidence = Math.max(edgeScore, devScore);
  const present =
    edgeDensity >= edgeThreshold || deviationFraction >= devFractionThreshold;

  return { present, edgeDensity, deviationFraction, confidence };
}

/* ────────────────────────────────────────────────────────────────────────────
   PR-E18 #3 / E18b: positive aura-marker detection (gmax/shadow/purified).

   For these three variants "caught" is signalled by a coloured AURA distinct
   from both the sprite and the page background; Pokemon GO renders the sprite
   itself faintly, so sprite-presence alone is not enough. We detect the aura
   POSITIVELY by a localized high-chroma blob in a SPECIFIC hue band inside a
   SPECIFIC region of the cell, tuned to the MEASURED signatures in the brief:

     SHADOW   purple flame, hue ~240-260, LOWER strip (esp bottom-left).
              flame chroma_max 0.545 vs empty-cell bg 0.169.
     PURIFIED teal-white starburst, hue ~160-175, LOWER-LEFT.
              burst chroma_max 0.51 vs missing 0.157, page bg 0.28.
     GMAX     red rose cloud, hue ~0-25 (RED, separate from pink bg hue
              ~325-340), TOP strip. rose chroma_max 0.729 at hue ~13.

   MEGA is NOT here: PR-E18b. The user's Mega-tab screenshots confirm caught
   Megas render as plain full-colour sprites on the pink page background with NO
   aura marker (the faint pink-on-pink Diancie 0719 measures chroma_max 0.18,
   indistinguishable from the pink page bg 0.169). Mega is therefore classified
   by SPRITE PRESENCE on the local-cell-background path (estimateLocalCellBackground
   + edgeDensityFromRgba + localBgPresenceFromRgba), the same gate the binary
   variants use, wired in cellSpriteMatcher.ts. It does not go through detectAura.

   The page background on each deep-dex page is also tinted, but it is LOW chroma
   and UNIFORM, so the aura separates by (localized chroma SPIKE) + (hue band) +
   (cell region). Detection works on a max-chroma estimate inside the region: a
   uniform low-chroma background never produces an in-band high-chroma blob.
   ──────────────────────────────────────────────────────────────────────── */

/** Max-chroma / hue of a single pixel, where chroma is (max-min)/255 in 0-1. */
function pixelChromaHue(r: number, g: number, b: number): { chroma: number; hue: number | null } {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const chroma = (max - min) / 255;
  const h = pixelHueDeg(r, g, b);
  return { chroma, hue: h };
}

/** Region of the cell an aura marker is expected to occupy. */
type AuraRegion = "top" | "lower" | "lowerLeft";

/** Per-variant aura marker spec: hue band, cell region, min localized chroma. */
interface AuraSpec {
  /** Inclusive hue band [lo, hi] in degrees. */
  hueBand: [number, number];
  region: AuraRegion;
  /** Min localized chroma for an in-band pixel to count as marker. */
  minChroma: number;
  /** Min fraction of region pixels that are marker pixels to call it present. */
  minMarkerFraction: number;
}

/**
 * MEASURED aura specs (see brief). minChroma sits between the empty/background
 * chroma and the measured marker chroma for each variant; minMarkerFraction is
 * kept small because the aura covers only part of its region. Mega is NOT here
 * (PR-E18b): it has no aura and is classified by sprite presence instead.
 */
const AURA_SPECS: Partial<Record<PokedexVariant, AuraSpec>> = {
  shadow: { hueBand: [240, 260], region: "lower", minChroma: 0.3, minMarkerFraction: 0.01 },
  purified: { hueBand: [160, 175], region: "lowerLeft", minChroma: 0.3, minMarkerFraction: 0.01 },
  gmax: { hueBand: [0, 25], region: "top", minChroma: 0.35, minMarkerFraction: 0.01 },
};

/** Return the [y0,y1,x0,x1] interior bounds (px) for an aura region of a cell. */
function auraRegionBounds(
  region: AuraRegion,
  width: number,
  height: number,
): { y0: number; y1: number; x0: number; x1: number } {
  switch (region) {
    case "top":
      return { y0: 0, y1: Math.floor(height * 0.45), x0: 0, x1: width };
    case "lower":
      return { y0: Math.floor(height * 0.55), y1: height, x0: 0, x1: width };
    case "lowerLeft":
    default:
      return { y0: Math.floor(height * 0.55), y1: height, x0: 0, x1: Math.floor(width * 0.6) };
  }
}

export interface AuraDetectionResult {
  present: boolean;
  confidence: number;
}

/** Inclusive circular hue-band test (handles bands that do not wrap here). */
function hueInBand(hue: number, lo: number, hi: number): boolean {
  return hue >= lo && hue <= hi;
}

/**
 * Detect the positive caught-aura marker for a variant inside its cell crop.
 *
 * For shadow/purified/gmax: count pixels inside the marker REGION whose hue is
 * in the variant's measured BAND and whose localized chroma clears `minChroma`.
 * The aura is present when the marker fraction over the region clears
 * `minMarkerFraction`. A uniform low-chroma page background never produces an
 * in-band high-chroma blob, so an empty coloured cell reads absent.
 *
 * Returns {present:false, confidence:0} for variants with no aura spec (which
 * now includes mega, PR-E18b: Mega has no aura and is classified by sprite
 * presence in cellSpriteMatcher.ts instead).
 *
 * Pure function over an RGBA crop (the sprite/cell region, already cropped).
 */
export function detectAura(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  variant: PokedexVariant,
): AuraDetectionResult {
  const spec = AURA_SPECS[variant];
  if (!spec || width < 4 || height < 4) return { present: false, confidence: 0 };

  const { y0, y1, x0, x1 } = auraRegionBounds(spec.region, width, height);

  // Measured hue-band path (shadow/purified/gmax).
  const [lo, hi] = spec.hueBand;
  let marker = 0;
  let total = 0;
  for (let y = y0; y < y1; y++) {
    for (let x = x0; x < x1; x++) {
      const i = (y * width + x) * 4;
      const { chroma, hue } = pixelChromaHue(data[i], data[i + 1], data[i + 2]);
      total++;
      if (hue != null && chroma >= spec.minChroma && hueInBand(hue, lo, hi)) {
        marker++;
      }
    }
  }
  const fraction = total > 0 ? marker / total : 0;
  const present = fraction >= spec.minMarkerFraction;
  const confidence = Math.min(1, fraction / (spec.minMarkerFraction * 8));
  return { present, confidence };
}

/** Variants that carry a positive caught-aura marker (PR-E18 #3). */
export const AURA_VARIANTS: ReadonlySet<PokedexVariant> = new Set<PokedexVariant>(
  Object.keys(AURA_SPECS) as PokedexVariant[],
);

/** True when the named variant uses positive aura-marker detection. */
export function hasAuraMarker(variant: PokedexVariant): boolean {
  return AURA_VARIANTS.has(variant);
}

/* ────────────────────────────────────────────────────────────────────────────
   b18-pill: iOS section-index fast-scroll capsule detection and masking.

   The iOS UITableView section-index "fast-scroll capsule" overlays the
   rightmost column of the dex grid during slow scrolls. It is a translucent
   white/pale rounded-rect with two blue chevron icons (up + down) at hue
   ~210 degrees. It stays visible for the full duration of a slow scroll and
   only fades ~2 s after the user finger lifts.

   detectFastScrollPill scans the rightmost 15% of the full captured frame
   for pixels whose hue falls in the blue chevron band (~195-240 deg),
   saturation > 0.30, and luminance in the 0.18-0.82 range. These pixels are
   grouped into vertical clusters; the pill is present when two clusters with
   vertical separation >= 15 px are found (upper chevron + lower chevron).
   The returned bounding box covers both clusters with a small margin that
   encloses the surrounding capsule body.

   The three sprite/OCR analysers (analyzeSpriteHues, analyzeSpriteChroma,
   dexCellNumberPositionFromRgba) each accept an optional pillMask param.
   When a cell crop overlaps the pill, the caller maps the frame-space pill
   box into the cell coordinate space and passes it in. Every inner loop
   skips pixels whose (x, y) falls inside the mask.
   ──────────────────────────────────────────────────────────────────────── */

/**
 * Bounding box of the iOS fast-scroll capsule in pixel space (frame
 * coordinates, not normalised fractions). null when no pill was detected.
 */
export interface PillRegion {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** True when (r,g,b) matches the blue chevron colour inside the iOS fast-scroll capsule. */
function isBlueCapsulePixel(r: number, g: number, b: number): boolean {
  // The chevron arrows are a medium-blue: hue ~195-240 deg, moderate-to-high
  // saturation, not too dark (shadow) and not near-white (capsule body).
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  if (max < 40) return false;
  const d = max - min;
  const sat = max > 0 ? d / max : 0;
  if (sat < 0.30) return false;
  // Blue must dominate over both red and green.
  if (b <= g || b <= r) return false;
  // Hue in the blue-dominant case: max === b => h = (r - g) / d + 4, * 60.
  const hue = (((r - g) / d + 4) * 60 + 360) % 360;
  if (hue < 190 || hue > 245) return false;
  // Luminance guard: exclude near-black shadow and near-white capsule fill.
  const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return lum >= 0.18 && lum <= 0.82;
}

/**
 * Detect the iOS section-index fast-scroll capsule in a full captured frame.
 *
 * Returns the bounding box (frame pixel coords) of the pill region, or null
 * when no pill is detected. Pure function; no DOM dependencies.
 */
export function detectFastScrollPill(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
): PillRegion | null {
  if (width < 10 || height < 40) return null;

  // Search only the rightmost 15% of the frame.
  const scanLeft = Math.floor(width * 0.85);

  // Collect rows that contain at least one blue-chevron pixel.
  const hitRows: number[] = [];
  for (let y = 0; y < height; y++) {
    for (let x = scanLeft; x < width; x++) {
      const i = (y * width + x) * 4;
      if (isBlueCapsulePixel(data[i], data[i + 1], data[i + 2])) {
        hitRows.push(y);
        break; // one hit per row is enough
      }
    }
  }

  if (hitRows.length < 4) return null;

  // Group consecutive hit rows into clusters (gap tolerance = 8 px).
  const clusters: { top: number; bottom: number }[] = [];
  let clTop = hitRows[0];
  let clBot = hitRows[0];
  for (let i = 1; i < hitRows.length; i++) {
    if (hitRows[i] - clBot <= 8) {
      clBot = hitRows[i];
    } else {
      if (clBot - clTop >= 2) clusters.push({ top: clTop, bottom: clBot });
      clTop = hitRows[i];
      clBot = hitRows[i];
    }
  }
  if (clBot - clTop >= 2) clusters.push({ top: clTop, bottom: clBot });

  if (clusters.length < 2) return null;

  const topCluster = clusters[0];
  const botCluster = clusters[clusters.length - 1];

  // Require minimum vertical separation to avoid a single large cluster.
  if (botCluster.top - topCluster.bottom < 15) return null;

  // Require a BOUNDED vertical extent. The real capsule is a compact rounded
  // rect — its two chevrons sit close together (the position number between
  // them), well under ~0.10 of the frame height. On a SHINY dex page the
  // rightmost column is full of blue-toned shiny sprites that also trip
  // isBlueCapsulePixel, so clusters[0]/clusters[last] land on shiny bodies and
  // span a big chunk of the column: device 2026-07-13 showed a full-height
  // h≈1890 phantom, and after the first cap (0.4) the residual noise pills still
  // spanned ~0.30 (h≈574 of a ~1918px frame), masking the 4th column and
  // stalling/false-negativing its cells (e.g. dex 28). A capsule can't be this
  // tall: if the outermost clusters exceed a plausible capsule height, the right
  // column is sprite noise, not a capsule, so emit no pill (the cells then
  // classify on full pixels and apply in reading order). 0.15 rejects the
  // ~0.30 shiny-noise span decisively while leaving ~2x margin over a real
  // capsule (~0.05-0.08).
  const MAX_PILL_SPAN_FRAC = 0.15;
  if (botCluster.bottom - topCluster.top > height * MAX_PILL_SPAN_FRAC) return null;

  // Expand the box to cover the surrounding capsule body.
  const pillY = Math.max(0, topCluster.top - 20);
  const pillH = Math.min(height - 1, botCluster.bottom + 20) - pillY + 1;
  const pillX = Math.max(0, scanLeft - 15);
  const pillW = width - pillX;

  return { x: pillX, y: pillY, w: pillW, h: pillH };
}

/**
 * Return true when pixel (px, py) in cell coordinates falls inside the pill
 * region. Inline so the per-pixel inner loops stay tight.
 */
function pixelInPill(px: number, py: number, pill: PillRegion): boolean {
  return (
    px >= pill.x && px < pill.x + pill.w &&
    py >= pill.y && py < pill.y + pill.h
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   PR-G: perceptual-hash helpers for video frame dedupe.
   See pogo_screen_ingest_spec.md §11.3 layer 2.

   16x16 average-hash:
     1. Downscale to a 16x16 luminance grid.
     2. Compute the mean luminance over all 256 cells.
     3. Each cell becomes 1 if its luminance is at or above the mean,
        else 0. Pack the 256 bits into 64 hex chars.

   Hamming distance between two 256-bit fingerprints is the count of
   differing bits. A "near-duplicate" threshold of <= 8 bits is the
   pvpoke-style default; tune at the caller if needed.
   ──────────────────────────────────────────────────────────────────────── */

export function phash16FromRgba(buf: { data: Uint8ClampedArray; width: number; height: number }): string {
  const { data, width, height } = buf;
  const grid = new Float32Array(16 * 16);
  const counts = new Uint32Array(16 * 16);
  for (let y = 0; y < height; y++) {
    const gy = Math.min(15, Math.floor((y * 16) / Math.max(1, height)));
    for (let x = 0; x < width; x++) {
      const gx = Math.min(15, Math.floor((x * 16) / Math.max(1, width)));
      const i = (y * width + x) * 4;
      const lum = 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
      const g = gy * 16 + gx;
      grid[g] += lum;
      counts[g] += 1;
    }
  }
  for (let i = 0; i < 256; i++) {
    if (counts[i] > 0) grid[i] /= counts[i];
  }
  let mean = 0;
  for (let i = 0; i < 256; i++) mean += grid[i];
  mean /= 256;
  let hex = "";
  for (let nib = 0; nib < 64; nib++) {
    let v = 0;
    for (let bit = 0; bit < 4; bit++) {
      const idx = nib * 4 + bit;
      if (grid[idx] >= mean) v |= (1 << (3 - bit));
    }
    hex += v.toString(16);
  }
  return hex;
}

export function phashHamming(a: string, b: string): number {
  if (a.length !== b.length) return Number.POSITIVE_INFINITY;
  let d = 0;
  for (let i = 0; i < a.length; i++) {
    const av = parseInt(a[i], 16);
    const bv = parseInt(b[i], 16);
    let x = (av ^ bv) & 0xf;
    while (x) { d += x & 1; x >>= 1; }
  }
  return d;
}

export function isPerceptualDuplicate(candidate: string, seen: string[] | Set<string>, threshold = 8): boolean {
  const arr = Array.isArray(seen) ? seen : Array.from(seen);
  for (const s of arr) {
    if (phashHamming(candidate, s) <= threshold) return true;
  }
  return false;
}
